import React from 'react';
import { Card, Title, LineChart, Text, Subtitle, Grid, Col, Flex, Legend } from '@tremor/react';

export default function ParWlGrowthChart() {
  // Data for PAR WL growth projection
  // Assumes 20-Pay policy for a newborn with PUA dividend option
  // Shows growth to age 65
  const parWlData = [
    { age: 0, guaranteedCashValue: 0, totalCashValue: 0, deathBenefit: 100000 },
    { age: 5, guaranteedCashValue: 2500, totalCashValue: 3200, deathBenefit: 105000 },
    { age: 10, guaranteedCashValue: 7800, totalCashValue: 11500, deathBenefit: 115000 },
    { age: 15, guaranteedCashValue: 14500, totalCashValue: 24000, deathBenefit: 130000 },
    { age: 20, guaranteedCashValue: 22000, totalCashValue: 42000, deathBenefit: 155000 },
    { age: 25, guaranteedCashValue: 28000, totalCashValue: 65000, deathBenefit: 185000 },
    { age: 30, guaranteedCashValue: 35000, totalCashValue: 95000, deathBenefit: 225000 },
    { age: 35, guaranteedCashValue: 42000, totalCashValue: 135000, deathBenefit: 275000 },
    { age: 40, guaranteedCashValue: 50000, totalCashValue: 190000, deathBenefit: 340000 },
    { age: 45, guaranteedCashValue: 58000, totalCashValue: 260000, deathBenefit: 420000 },
    { age: 50, guaranteedCashValue: 67000, totalCashValue: 350000, deathBenefit: 520000 },
    { age: 55, guaranteedCashValue: 76000, totalCashValue: 470000, deathBenefit: 650000 },
    { age: 60, guaranteedCashValue: 86000, totalCashValue: 620000, deathBenefit: 810000 },
    { age: 65, guaranteedCashValue: 97000, totalCashValue: 820000, deathBenefit: 1020000 },
  ];

  // Key ages for milestone comparison
  const keyAges = [20, 40, 65];
  const milestoneData = parWlData.filter(item => keyAges.includes(item.age));

  return (
    <Card className="mt-8 mb-8 bg-green-50 border border-green-100">
      <Title className="text-green-800 mb-2">Participating Whole Life Insurance Growth</Title>
      <Subtitle className="text-green-700 mb-4">
        20-Pay Policy with PUA Dividend Option (5.5% Dividend Scale)
      </Subtitle>

      <Flex className="mt-4 mb-2 justify-center">
        <Legend
          className="max-w-lg"
          categories={["Guaranteed Cash Value", "Total Cash Value", "Death Benefit"]}
          colors={["emerald", "green", "teal"]}
        />
      </Flex>
      
      <LineChart
        className="h-72 mt-4"
        data={parWlData}
        index="age"
        categories={["guaranteedCashValue", "totalCashValue", "deathBenefit"]}
        colors={["emerald", "green", "teal"]}
        valueFormatter={(number) => `$${number.toLocaleString()}`}
        showLegend={false}
        showGridLines={true}
        showAnimation={true}
        curveType="monotone"
        customTooltip={(props) => {
          const { payload, active } = props;
          if (!active || !payload) return null;
          
          const age = payload[0]?.payload.age;
          const guaranteedCashValue = payload[0]?.value;
          const totalCashValue = payload[1]?.value;
          const deathBenefit = payload[2]?.value;
          
          return (
            <div className="p-2 border border-tremor-border shadow-tremor-dropdown rounded-tremor-default bg-tremor-background">
              <div className="font-semibold text-tremor-content-emphasis">Age {age}</div>
              <div className="flex flex-col gap-1 mt-2">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-emerald-500"></span>
                  <span className="text-tremor-content">Guaranteed: ${guaranteedCashValue?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-green-500"></span>
                  <span className="text-tremor-content">Total Cash Value: ${totalCashValue?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-teal-500"></span>
                  <span className="text-tremor-content">Death Benefit: ${deathBenefit?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          );
        }}
      />
      
      <Title className="text-green-800 mt-8 mb-4">Key Milestones</Title>
      <Grid numItems={1} numItemsSm={3} numItemsLg={3} className="gap-4 mt-2">
        {milestoneData.map((data) => (
          <Card key={data.age} className="bg-white shadow-sm">
            <Text className="text-center text-green-700 font-medium">Age {data.age}</Text>
            <div className="mt-2 space-y-1">
              <Flex justifyContent="between">
                <Text className="text-xs text-gray-600">Guaranteed:</Text>
                <Text className="text-xs font-medium">${data.guaranteedCashValue.toLocaleString()}</Text>
              </Flex>
              <Flex justifyContent="between">
                <Text className="text-xs text-gray-600">Total Cash Value:</Text>
                <Text className="text-xs font-medium">${data.totalCashValue.toLocaleString()}</Text>
              </Flex>
              <Flex justifyContent="between">
                <Text className="text-xs text-gray-600">Death Benefit:</Text>
                <Text className="text-xs font-medium">${data.deathBenefit.toLocaleString()}</Text>
              </Flex>
            </div>
          </Card>
        ))}
      </Grid>
      
      <Text className="text-xs text-gray-500 mt-6">
        Note: This chart illustrates the projected growth of a Participating Whole Life insurance policy purchased for a newborn.
        The "Total Cash Value" includes both guaranteed cash value and non-guaranteed dividends using the Paid-Up Additions (PUA) option.
        Dividend projections are based on a 5.5% dividend scale interest rate and are not guaranteed. Actual results may vary.
      </Text>
    </Card>
  );
}
